var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import * as React from 'react';
import { ThemeProvider } from '../theme';
import { defaultQuantumTheme } from '../theme/default-theme';
import { useMediaQuery } from '../use-media-query';
import { DEFAULT_COLOR_MODE, QuantumContext } from './quantum-context';
var DEFAULT_COLOR_MODE_STORAGE_KEY = 'quantum:color-mode';
export var PREFERS_DARK_MODE_QUERY = '(prefers-color-scheme: dark)';
var validColorModes = new Set(['light', 'dark', 'system']);
export var getCurrentColorModeFromStorage = function () {
    try {
        return window.localStorage.getItem(DEFAULT_COLOR_MODE_STORAGE_KEY) || DEFAULT_COLOR_MODE;
    }
    catch (_err) {
        return DEFAULT_COLOR_MODE;
    }
};
var setCurrentColorModeInStorage = function (mode) {
    try {
        window.localStorage.setItem(DEFAULT_COLOR_MODE_STORAGE_KEY, mode);
    }
    catch (_err) {
        // ..
    }
};
var computePaletteMode = function (currentColorMode, prefersDarkMode) {
    return currentColorMode === 'system' ? (prefersDarkMode ? 'dark' : 'light') : currentColorMode;
};
var isValidColorMode = function (colorMode) {
    return validColorModes.has(colorMode);
};
var computeValidColorMode = function (colorMode) {
    return isValidColorMode(colorMode) ? colorMode : DEFAULT_COLOR_MODE;
};
export var QuantumProvider = function (_a) {
    var _b = _a.theme, theme = _b === void 0 ? defaultQuantumTheme : _b, children = _a.children, onColorModeChange = _a.onColorModeChange, propColorMode = _a.colorMode, defaultColorMode = _a.defaultColorMode;
    var _c = __read(React.useState(function () {
        return propColorMode || defaultColorMode || getCurrentColorModeFromStorage();
    }), 2), colorMode = _c[0], setColorMode = _c[1];
    var prefersDarkMode = useMediaQuery(PREFERS_DARK_MODE_QUERY);
    var currentColorMode = computeValidColorMode(propColorMode || colorMode);
    React.useEffect(function () {
        var _a;
        var mode = computePaletteMode(currentColorMode, prefersDarkMode);
        (_a = document.querySelector('html')) === null || _a === void 0 ? void 0 : _a.setAttribute('data-theme', mode);
    }, [currentColorMode, prefersDarkMode]);
    var activeTheme = React.useMemo(function () {
        var mode = computePaletteMode(currentColorMode, prefersDarkMode);
        return theme[mode] || theme[DEFAULT_COLOR_MODE];
    }, [prefersDarkMode, currentColorMode, theme]);
    var handleSetColorMode = React.useCallback(function (mode) {
        setColorMode(mode);
        setCurrentColorModeInStorage(mode);
        if (onColorModeChange) {
            onColorModeChange(mode);
        }
    }, [setColorMode, onColorModeChange]);
    var ctxValue = React.useMemo(function () { return ({
        colorMode: currentColorMode,
        setColorMode: handleSetColorMode,
        theme: activeTheme,
    }); }, [currentColorMode, activeTheme, handleSetColorMode]);
    return (React.createElement(QuantumContext.Provider, { value: ctxValue },
        React.createElement(ThemeProvider, { theme: activeTheme }, typeof children === 'function' ? children(ctxValue) : children)));
};
